import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Typography, Space, Button, Input, Segmented, Alert, Tooltip, message,
  Modal, Descriptions, Empty,
} from 'antd';
import {
  UserDeleteOutlined, ClockCircleOutlined, StopOutlined, DownloadOutlined, ReloadOutlined, MailOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  adminService,
  type InactiveCustomer,
  type InactiveCustomerReport,
  type CustomerOrder,
} from '../../services/adminService';

const { Title, Text } = Typography;

type BucketKey = 'all' | '30' | '60' | '90' | 'never';

interface InactiveCustomersProps {
  /** When rendered inside another page (e.g. a User Management tab), drop the
   *  outer padding and the redundant page title. */
  embedded?: boolean;
}

const InactiveCustomers: React.FC<InactiveCustomersProps> = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<InactiveCustomerReport | null>(null);
  const [bucket, setBucket] = useState<BucketKey>('30');
  const [search, setSearch] = useState('');

  // Drill-down modal: selected customer + their web-order history.
  const [selected, setSelected] = useState<InactiveCustomer | null>(null);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [emailing, setEmailing] = useState(false);
  const emailReport = async () => {
    setEmailing(true);
    try {
      const { recipients, message: msg } = await adminService.emailInactiveCustomers();
      message.success(recipients.length ? `Report emailed to ${recipients.join(', ')}` : msg);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to email the report');
    } finally {
      setEmailing(false);
    }
  };

  const openCustomer = async (c: InactiveCustomer) => {
    setSelected(c);
    setOrders([]);
    setOrdersLoading(true);
    try {
      setOrders(await adminService.getCustomerOrders(c.traderCode));
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load purchase history');
    } finally {
      setOrdersLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      setReport(await adminService.getInactiveCustomers());
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load inactive customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const inBucket = (c: InactiveCustomer, key: BucketKey): boolean => {
    if (key === 'never') return c.daysInactive == null;
    if (key === 'all') return true;
    const threshold = Number(key);
    return c.daysInactive != null && c.daysInactive >= threshold;
  };

  const filtered = useMemo(() => {
    const all = report?.customers ?? [];
    const q = search.trim().toLowerCase();
    return all
      .filter((c) => inBucket(c, bucket))
      .filter((c) =>
        !q ||
        c.traderName.toLowerCase().includes(q) ||
        c.traderCode.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.contact.toLowerCase().includes(q)
      );
  }, [report, bucket, search]);

  const exportCsv = () => {
    const header = ['Trader Code', 'Name', 'Contact', 'Phone', 'Email', 'Price Level', 'Last Activity', 'Days Inactive', 'Source'];
    const rows = filtered.map((c) => [
      c.traderCode, c.traderName, c.contact, c.phone, c.email, c.priceLevel,
      c.lastActivity ? dayjs(c.lastActivity).format('YYYY-MM-DD') : 'Never',
      c.daysInactive == null ? 'Never' : String(c.daysInactive),
      c.source,
    ]);
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inactive-customers-${bucket}-${dayjs().format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const b = report?.buckets;

  const cards: { key: BucketKey; title: string; value: number; icon: React.ReactNode; color: string }[] = [
    { key: '30', title: 'Inactive 30+ days', value: b?.inactive30 ?? 0, icon: <ClockCircleOutlined />, color: '#faad14' },
    { key: '60', title: 'Inactive 60+ days', value: b?.inactive60 ?? 0, icon: <ClockCircleOutlined />, color: '#fa8c16' },
    { key: '90', title: 'Inactive 90+ days', value: b?.inactive90 ?? 0, icon: <StopOutlined />, color: '#f5222d' },
    { key: 'never', title: 'Never ordered', value: b?.never ?? 0, icon: <UserDeleteOutlined />, color: '#8c8c8c' },
  ];

  const daysTag = (c: InactiveCustomer) => {
    if (c.daysInactive == null) return <Tag color="default">Never</Tag>;
    const d = c.daysInactive;
    const color = d >= 90 ? 'red' : d >= 60 ? 'volcano' : d >= 30 ? 'gold' : 'green';
    return <Tag color={color}>{d} days</Tag>;
  };

  const columns = [
    {
      title: 'Customer', key: 'customer',
      render: (_: any, c: InactiveCustomer) => (
        <Space direction="vertical" size={0}>
          <Text strong>{c.traderName || c.traderCode}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{c.traderCode}{c.priceLevel ? ` · ${c.priceLevel}` : ''}</Text>
        </Space>
      ),
    },
    {
      title: 'Contact', key: 'contact', responsive: ['md'] as any,
      render: (_: any, c: InactiveCustomer) => (
        <Space direction="vertical" size={0}>
          {c.contact ? <Text>{c.contact}</Text> : null}
          {c.phone ? <Text type="secondary" style={{ fontSize: 12 }}>{c.phone}</Text> : null}
          {c.email ? <Text type="secondary" style={{ fontSize: 12 }}>{c.email}</Text> : null}
          {!c.contact && !c.phone && !c.email ? <Text type="secondary">—</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Last Activity', dataIndex: 'lastActivity', key: 'lastActivity',
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : <Text type="secondary">—</Text>,
    },
    {
      title: 'Inactive', key: 'daysInactive', align: 'right' as const,
      sorter: (a: InactiveCustomer, z: InactiveCustomer) =>
        (a.daysInactive ?? Number.MAX_SAFE_INTEGER) - (z.daysInactive ?? Number.MAX_SAFE_INTEGER),
      render: (_: any, c: InactiveCustomer) => daysTag(c),
    },
    {
      title: 'Source', dataIndex: 'source', key: 'source', responsive: ['lg'] as any,
      render: (s: InactiveCustomer['source']) =>
        s === 'erp' ? <Tag color="blue">ERP</Tag>
          : s === 'web' ? <Tag color="green">Web</Tag>
            : <Tag>—</Tag>,
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      <Space style={{ width: '100%', justifyContent: embedded ? 'flex-end' : 'space-between', marginBottom: 16 }} wrap>
        {!embedded && <Title level={3} style={{ margin: 0 }}>Inactive Customers</Title>}
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Tooltip title="This report is emailed to the admin automatically on the 1st of each month. Click to send it now.">
            <Button icon={<MailOutlined />} onClick={emailReport} loading={emailing} disabled={!report}>
              Send Now
            </Button>
          </Tooltip>
          <Button type="primary" icon={<DownloadOutlined />} onClick={exportCsv} disabled={filtered.length === 0}>
            Export CSV
          </Button>
        </Space>
      </Space>

      {b && b.fromErpFeed === 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Showing web-order activity only"
          description="Offline/ERP sales aren't in the web database. Once the ERP feed populates the sales-summary table, offline purchases will count towards each customer's last-activity date. Until then, customers who only buy offline appear as “Never ordered”."
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {cards.map((c) => (
          <Col xs={24} sm={12} lg={6} key={c.key}>
            <Card
              hoverable
              onClick={() => setBucket(c.key)}
              style={{ borderColor: bucket === c.key ? c.color : undefined, borderWidth: bucket === c.key ? 2 : 1 }}
            >
              <Statistic
                title={c.title}
                value={c.value}
                valueStyle={{ color: c.color }}
                prefix={c.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
          <Segmented
            value={bucket}
            onChange={(v) => setBucket(v as BucketKey)}
            options={[
              { label: 'All', value: 'all' },
              { label: '30+ days', value: '30' },
              { label: '60+ days', value: '60' },
              { label: '90+ days', value: '90' },
              { label: 'Never', value: 'never' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Search name, code, email…"
            style={{ maxWidth: 320 }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Space>

        <Tooltip title="Total web-enabled customers">
          <Text type="secondary">{filtered.length} of {b?.total ?? 0} customers</Text>
        </Tooltip>

        <Table
          rowKey="traderCode"
          dataSource={filtered}
          columns={columns}
          loading={loading}
          size="middle"
          scroll={{ x: 720 }}
          style={{ marginTop: 12 }}
          onRow={(record) => ({
            onClick: () => openCustomer(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} customers` }}
          locale={{ emptyText: 'No customers in this segment.' }}
        />
      </Card>

      <Modal
        title={selected ? `Customer — ${selected.traderName || selected.traderCode}` : 'Customer'}
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={<Button onClick={() => setSelected(null)}>Close</Button>}
        width={760}
        destroyOnHidden
      >
        {selected && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Trader Code">{selected.traderCode}</Descriptions.Item>
              <Descriptions.Item label="Price Level">{selected.priceLevel || '—'}</Descriptions.Item>
              <Descriptions.Item label="Contact">{selected.contact || '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selected.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>{selected.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Last Activity">
                {selected.lastActivity ? dayjs(selected.lastActivity).format('DD MMM YYYY') : 'Never'}
              </Descriptions.Item>
              <Descriptions.Item label="Days Inactive">
                {selected.daysInactive == null ? 'Never ordered' : `${selected.daysInactive} days`}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 20 }}>
              Purchase History ({orders.length} order{orders.length === 1 ? '' : 's'})
            </Title>
            <Table
              rowKey="RefNo"
              size="small"
              loading={ordersLoading}
              dataSource={orders}
              pagination={false}
              scroll={{ x: 640 }}
              locale={{ emptyText: <Empty description="No web orders for this customer" /> }}
              columns={[
                { title: 'Ref No', dataIndex: 'RefNo', key: 'RefNo' },
                {
                  title: 'Items', dataIndex: 'ItemsSummary', key: 'ItemsSummary', ellipsis: true,
                  render: (v: string | null) => v
                    ? <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 240 }}>{v}</Text>
                    : <Text type="secondary">—</Text>,
                },
                {
                  title: 'Date', dataIndex: 'DateIssued', key: 'DateIssued',
                  render: (d: string | null) => d ? dayjs(d).format('DD MMM YYYY') : '—',
                },
                {
                  title: 'Amount', dataIndex: 'TotalDueAmt', key: 'TotalDueAmt', align: 'right' as const,
                  render: (a: number) => `$${(Number(a) || 0).toFixed(2)}`,
                },
                {
                  title: 'Status', dataIndex: 'webStatus', key: 'webStatus',
                  render: (s: string) => <Tag>{s}</Tag>,
                },
                {
                  title: 'Promo', dataIndex: 'PromoCode', key: 'PromoCode',
                  render: (v: string | null) => v ? <Tag color="success">{v}</Tag> : <Text type="secondary">—</Text>,
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default InactiveCustomers;
